import { z } from "zod/v4";

import { applyDiscountTierRefinements } from "./discount-tiers.refinements";

/**
 * Sentinel upper bound for an open-ended tier — "this threshold and above".
 * Tiers store a concrete `maximumUnits`, so the top tier of a tiered discount
 * uses this rather than 0 or null. Lives here beside the tier DTO because both
 * the app and the AI service have to agree on the convention.
 */
export const DISCOUNT_MAX_UNITS = 1000000000;

const DiscountTierBaseSchema = z.object({
  id: z.uuid(),
  minimumUnits: z.number().default(1),
  maximumUnits: z.number().default(1),
  unitDiscount: z.number().default(1),
  // Buy X Get Y: how many units the discount applies to, per buy threshold.
  // 0 means "not a BXGY tier" — the Shopify Function keys off `> 0`
  bxgyGetQuantity: z.number().int().nonnegative().default(0),
  discountTitle: z.string().nullable().default(null),
  giftWithPurchaseTitle: z.string().nullable().default(null),
  isFreeShipping: z.boolean().default(false),
  isGiftWithPurchase: z.boolean().default(false),
  giftWithPurchaseProductId: z.string().nullish(),
  giftWithPurchaseVariantId: z.string().nullish(),
  // Legacy rows persist this JSON column as `null`; coerce null/undefined to
  // an empty array so DTO serialization (and write validation) never throws.
  // Uses preprocess (not .transform) so the field stays a plain `string[]` in
  // output schemas — zod-openapi can't serialize a transform in an output schema
  // (it breaks the external-API OpenAPI/docs build).
  giftWithPurchaseVariantIds: z.preprocess(
    (value) => value ?? [],
    z.string().array()
  ),
  autoAddGiftWithPurchase: z.boolean().default(false),
  giftWithPurchaseHandle: z.string().nullish(),
});

// DTO schema without refinements - safe for GET responses with existing data
export const DiscountTierDtoSchema = DiscountTierBaseSchema;

export type DiscountTierDto = z.infer<typeof DiscountTierDtoSchema>;

// Write schema with strict validation - for write endpoints only
export const WriteDiscountTierDtoSchema = applyDiscountTierRefinements(
  DiscountTierBaseSchema.extend({
    minimumUnits: z
      .number()
      .nonnegative("Minimum units must be non-negative")
      .default(1),
    maximumUnits: z
      .number()
      .nonnegative("Maximum units must be non-negative")
      .default(1),
    unitDiscount: z
      .number()
      .nonnegative("Unit discount must be non-negative")
      .default(1),
  })
);

// Write schema for creation - id is omitted (auto-generated by server)
export const CreateWriteDiscountTierDtoSchema = applyDiscountTierRefinements(
  DiscountTierBaseSchema.omit({ id: true }).extend({
    minimumUnits: z
      .number()
      .nonnegative("Minimum units must be non-negative")
      .default(1),
    maximumUnits: z
      .number()
      .nonnegative("Maximum units must be non-negative")
      .default(1),
    unitDiscount: z
      .number()
      .nonnegative("Unit discount must be non-negative")
      .default(1),
  })
);

// Write schema for update - id is optional (present for existing tiers, omitted for new ones)
export const PutWriteDiscountTierDtoSchema = applyDiscountTierRefinements(
  DiscountTierBaseSchema.extend({
    id: z.uuid().optional(),
    minimumUnits: z
      .number()
      .nonnegative("Minimum units must be non-negative")
      .default(1),
    maximumUnits: z
      .number()
      .nonnegative("Maximum units must be non-negative")
      .default(1),
    unitDiscount: z
      .number()
      .nonnegative("Unit discount must be non-negative")
      .default(1),
  })
);
